import { HugeiconsIcon } from "@hugeicons/react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { formatCurrency } from "@unfiddle/core/currency"
import { formatEstimateDate } from "@unfiddle/core/estimate/utils"
import { makeShortId } from "@unfiddle/core/id"
import { Badge } from "@unfiddle/ui/components/badge"
import { Button } from "@unfiddle/ui/components/button"
import { Card } from "@unfiddle/ui/components/card"
import { DrawerTrigger } from "@unfiddle/ui/components/drawer"
import { Expandable } from "@unfiddle/ui/components/expandable"
import { Icons } from "@unfiddle/ui/components/icons"
import {
   Menu,
   MenuItem,
   MenuPopup,
   MenuTrigger,
} from "@unfiddle/ui/components/menu"
import * as React from "react"
import { useAuth } from "@/auth/hooks"
import { ChatLink } from "@/chat/components/chat-link"
import { ClientSeverityIcon } from "@/client/components/client-severity-icon"
import { DeleteEstimateAlert } from "@/estimate/components/delete-estimate-alert"
import { UpdateEstimate } from "@/estimate/components/update-estimate"
import { useEstimate } from "@/estimate/hooks"
import { CreateEstimateItem } from "@/estimate/item/components/create-estimate-item"
import { useEstimateUnreadCount } from "@/estimate/message/read/queries"
import { useDeleteEstimate } from "@/estimate/mutations/use-delete-estimate"
import { CreateEstimateProcurement } from "@/estimate/procurement/components/create-estimate-procurement"
import {
   Header,
   HeaderBackButton,
   HeaderTitle,
} from "@/layout/components/header"
import { MainScrollArea } from "@/layout/components/main"
import { EstimateItem } from "@/routes/_authed/$workspaceId/_layout/(estimate)/-components/estimate-item"
import { EstimateProcurement } from "@/routes/_authed/$workspaceId/_layout/(estimate)/-components/estimate-procurement"
import { trpc } from "@/trpc"
import { SuspenseBoundary } from "@/ui/components/suspense-boundary"
import { UserAvatar } from "@/user/components/user-avatar"

export const Route = createFileRoute(
   "/_authed/$workspaceId/_layout/(estimate)/estimate/$estimateId/_layout/",
)({
   component: RouteComponent,
})

function RouteComponent() {
   const params = Route.useParams()
   const estimate = useEstimate()
   const navigate = useNavigate()
   const unreadCount = useEstimateUnreadCount(estimate.id)

   return (
      <>
         <Header className="md:flex md:px-1.75">
            <HeaderBackButton
               className={"mr-1.5 md:flex"}
               onClick={() =>
                  navigate({ to: "/$workspaceId/estimates", params })
               }
            />
            <HeaderTitle className="line-clamp-1">
               <span className="md:hidden">Прорахунок</span>{" "}
               {makeShortId(estimate.shortId)}
            </HeaderTitle>
            <Actions />
            <div className="ml-auto flex items-center gap-1.5 max-md:hidden">
               <ChatLink
                  unreadCount={unreadCount}
                  to="/$workspaceId/estimate/$estimateId/chat"
                  params={params}
               />
            </div>
         </Header>
         <MainScrollArea>
            <SuspenseBoundary
               fallback={null}
               errorComponent={null}
            >
               <TotalProfit />
            </SuspenseBoundary>
            <div className="mt-2 mb-3 flex gap-2">
               <p className="font-semibold text-xl md:text-2xl">
                  {estimate.name}
               </p>
               <ChatLink
                  unreadCount={unreadCount}
                  to="/$workspaceId/estimate/$estimateId/chat"
                  params={params}
                  className="-mt-1.5 ml-auto md:hidden"
               />
            </div>
            <p className="mb-1 whitespace-pre-wrap">{estimate.note}</p>
            <div className="mb-6 grid grid-cols-[40%_1fr] gap-y-4 lg:hidden">
               <section className="group/section">
                  <p className="text-muted text-sm">Ціна</p>
                  <p className="mt-1.5 font-medium font-mono text-lg">
                     {estimate.sellingPrice
                        ? formatCurrency(estimate.sellingPrice, {
                             currency: estimate.currency,
                          })
                        : "—"}
                  </p>
               </section>
               <section className="group/section">
                  <p className="text-muted text-sm">Клієнт</p>
                  <p className="mt-1.5 flex items-center gap-2">
                     {estimate.client ? (
                        <>
                           <ClientSeverityIcon
                              className="-mb-0.5"
                              severity={estimate.client.severity}
                           />
                           {estimate.client.name}
                        </>
                     ) : (
                        "—"
                     )}
                  </p>
               </section>
               <section className="group/section">
                  <p className="text-muted text-sm">Створене</p>
                  <p className="mt-2 flex items-center gap-2">
                     <UserAvatar
                        size={22}
                        user={estimate.creator}
                     />
                     {estimate.creator.name} —{" "}
                     {new Date(estimate.createdAt).getDate() ===
                     new Date().getDate() ? (
                        <span className="max-md:hidden">Сьогодні о</span>
                     ) : null}
                     {formatEstimateDate(estimate.createdAt)}
                  </p>
               </section>
            </div>
            <div className="mt-5">
               <div className="mb-2 flex items-center justify-between">
                  <p className="font-medium text-lg">Товари</p>
                  <CreateEstimateItem>
                     <DrawerTrigger
                        render={
                           <Button
                              variant={"secondary"}
                              size="sm"
                           >
                              <HugeiconsIcon icon={Icons.plus} />
                              Додати
                           </Button>
                        }
                     />
                  </CreateEstimateItem>
               </div>
               <div className="relative">
                  {estimate.items.map((item) => (
                     <EstimateItem
                        key={item.id}
                        item={item}
                     />
                  ))}
               </div>
            </div>
            <div className="relative mt-5 min-h-62.5">
               <div className="my-2 flex items-center justify-between">
                  <p className="font-medium text-lg">Закупівлі</p>
                  <CreateEstimateProcurement />
               </div>
               <SuspenseBoundary>
                  <Procurements />
               </SuspenseBoundary>
            </div>
         </MainScrollArea>
      </>
   )
}

function TotalProfit() {
   const params = Route.useParams()
   const estimate = useEstimate()
   const query = useSuspenseQuery(
      trpc.estimateProcurement.list.queryOptions(params),
   )
   const procurements = query.data

   const totalProfit = procurements.reduce(
      (acc, p) =>
         acc + ((estimate.sellingPrice ?? 0) - p.purchasePrice) * p.quantity,
      0,
   )

   return (
      <Expandable expanded={totalProfit > 0}>
         <Badge
            variant={"success"}
            className="mb-1.5 text-base"
         >
            Профіт:{" "}
            {`${formatCurrency(totalProfit as number, {
               currency: estimate.currency,
            })}`}{" "}
         </Badge>
      </Expandable>
   )
}

function Actions() {
   const params = Route.useParams()
   const auth = useAuth()
   const estimate = useEstimate()
   const deleteItem = useDeleteEstimate()

   const [editOpen, setEditOpen] = React.useState(false)
   const [_archiveAlertOpen, _setArchiveAlertOpen] = React.useState(false)
   const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false)
   const menuTriggerRef = React.useRef<HTMLButtonElement>(null)

   return (
      <>
         <Menu>
            <MenuTrigger
               render={
                  <Button
                     variant={"ghost"}
                     kind={"icon"}
                     className="md:ml-2"
                  >
                     <HugeiconsIcon
                        icon={Icons.ellipsisHorizontal}
                        className="size-5.5"
                     />
                  </Button>
               }
            />
            <MenuPopup align="start">
               <MenuItem
                  onClick={() => {
                     setEditOpen(true)
                  }}
               >
                  <HugeiconsIcon icon={Icons.pencil} />
                  Редагувати
               </MenuItem>
               {auth.workspace.role === "owner" ||
               auth.workspace.role === "admin" ? (
                  <MenuItem
                     destructive
                     onClick={() => setDeleteAlertOpen(true)}
                  >
                     <HugeiconsIcon icon={Icons.trash} />
                     Видалити
                  </MenuItem>
               ) : null}
            </MenuPopup>
         </Menu>
         <UpdateEstimate
            open={editOpen}
            setOpen={setEditOpen}
            estimateId={estimate.id}
            finalFocus={menuTriggerRef}
         />
         <DeleteEstimateAlert
            open={deleteAlertOpen}
            onOpenChange={setDeleteAlertOpen}
            estimateName={estimate.name}
            finalFocus={menuTriggerRef}
            action={() =>
               deleteItem.mutate({
                  estimateId: estimate.id,
                  workspaceId: params.workspaceId,
               })
            }
         />
      </>
   )
}

function Procurements() {
   const params = Route.useParams()
   const query = useSuspenseQuery(
      trpc.estimateProcurement.list.queryOptions(params),
   )

   if (query.data.length === 0)
      return <p className="font-medium text-muted">Ще немає закупівель.</p>

   return (
      <Card className="relative mt-2 p-0">
         {query.data.map((p) => (
            <EstimateProcurement
               key={p.id}
               estimateProcurement={p}
            />
         ))}
      </Card>
   )
}
